import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthLayout from "@/components/layout/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Smartphone, ShieldCheck, FileText, CheckCircle2,
  Loader2, ArrowRight, ArrowLeft, RotateCcw,
} from "lucide-react";
import { trackEvent } from "@/lib/analytics";

/**
 * Phase 7.30f — Mobile-OTP-only signup/login via hitch.zone.
 *
 * Replaces the old email/password + retailer-KYC form. The same wizard
 * handles existing users (direct dashboard) and new users (create + sync).
 *
 *   1. Mobile      → POST /auth/hitch/send-otp
 *   2. OTP         → POST /auth/hitch/verify-otp
 *                    → issues JWT, returns {is_new_user, hitch_kyc}
 *                    → if verified upstream: skip to dashboard
 *                    → else: advance to PAN
 *   3. PAN         → POST /auth/hitch/pan-verify (uses cached hitch session)
 *                    → re-pulls KYC %, promotes when ≥ threshold
 *   4. Done        → /dashboard
 */

const STEPS = [
  { id: "mobile", label: "Mobile", icon: Smartphone },
  { id: "otp", label: "OTP", icon: ShieldCheck },
  { id: "pan", label: "PAN", icon: FileText },
  { id: "done", label: "Done", icon: CheckCircle2 },
];

function StepBar({ step }) {
  const idx = STEPS.findIndex((s) => s.id === step);
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const active = i === idx;
        const done = i < idx;
        return (
          <div key={s.id} className="flex items-center gap-2">
            <div
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs border transition-colors ${
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : done
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                  : "bg-muted/40 border-border text-muted-foreground"
              }`}
              data-testid={`step-${s.id}`}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={1.7} />
              <span className="font-medium">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
          </div>
        );
      })}
    </div>
  );
}

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();

  const presetMobile = (() => {
    const raw = (searchParams.get("mobile") || "").replace(/\D/g, "");
    return /^[6-9]\d{9}$/.test(raw) ? raw : "";
  })();

  const [step, setStep] = useState("mobile");
  const [mobile, setMobile] = useState(presetMobile);
  const [otp, setOtp] = useState("");
  const [pan, setPan] = useState("");
  const [refCode, setRefCode] = useState(searchParams.get("ref_code") || "");
  const [loading, setLoading] = useState(false);
  const [kycInfo, setKycInfo] = useState(null); // {verification_percentage, threshold, ...}
  const [resultUser, setResultUser] = useState(null);
  // Resend cooldown — hitch.zone enforces ~60s server-side, we surface 30s in UI
  // so the user has a clear countdown rather than spam-clicking.
  const [resendIn, setResendIn] = useState(0);

  // 1. Mobile → send OTP
  const sendOtp = async ({ silent = false } = {}) => {
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      toast.error("Enter a valid 10-digit Indian mobile");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/hitch/send-otp", { mobile });
      if (!silent) toast.success("OTP sent to your mobile via hitch.zone");
      else toast.success("OTP re-sent");
      if (!silent) trackEvent("signup_started", { mobile: mobile.slice(0, 4) + "****" });
      setStep("otp");
      setResendIn(30); // start 30s cooldown
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send OTP");
    } finally {
      setLoading(false);
    }
  };

  // 2. OTP → verify, login or create user, sync KYC
  const verifyOtp = async () => {
    if (!/^\d{4,8}$/.test(otp)) {
      toast.error("Enter a valid OTP");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/hitch/verify-otp", {
        mobile,
        otp,
        ref_code: refCode || undefined,
      });
      // Persist the JWT — AuthContext.loadMe() will refresh user state.
      localStorage.setItem("payflex_token", data.access_token);
      setKycInfo(data.hitch_kyc);
      setResultUser(data.user);

      if (data.hitch_kyc?.auto_verified) {
        // ≥ threshold or hitch.kyc=true → go straight to dashboard.
        toast.success(
          `KYC verified at ${data.hitch_kyc.verification_percentage}% — you can start transacting.`
        );
        trackEvent("signup_completed", { method: "auto_verified", kyc_pct: data.hitch_kyc.verification_percentage });
        await refreshUser();
        navigate("/dashboard", { replace: true });
        return;
      }
      // Below threshold → ask for PAN.
      toast.message(
        `KYC at ${data.hitch_kyc.verification_percentage}% on hitch.zone. Verify your PAN to unlock transactions.`
      );
      setStep("pan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  // 3. PAN → /v4/kyc, re-pull KYC %, promote
  const verifyPan = async () => {
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan.toUpperCase())) {
      toast.error("Invalid PAN format (e.g. ABCDE1234F)");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/hitch/pan-verify", {
        pan_number: pan.toUpperCase(),
      });
      setKycInfo(data.hitch_kyc);
      setResultUser(data.user);
      if (data.hitch_kyc?.verified) {
        toast.success(
          `KYC verified — you're now at ${data.hitch_kyc.verification_percentage}% and can transact.`
        );
        trackEvent("signup_completed", { method: "pan_verified", kyc_pct: data.hitch_kyc.verification_percentage });
        await refreshUser();
        setStep("done");
        setTimeout(() => navigate("/dashboard", { replace: true }), 1200);
      } else {
        toast.warning(
          `PAN accepted but KYC is still at ${data.hitch_kyc.verification_percentage}%. Contact support to raise your verification.`
        );
        setStep("done");
      }
    } catch (err) {
      const code = err.response?.data?.detail?.code;
      if (code === "hitch_session_expired") {
        toast.error("Session expired. Re-verify your mobile OTP.");
        setStep("otp");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "PAN verification failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep("mobile");
    setOtp("");
    setPan("");
    setKycInfo(null);
    setResultUser(null);
  };

  // Auto-trigger send-otp if a mobile is preset via query string.
  useEffect(() => {
    if (presetMobile && step === "mobile") {
      // Don't auto-send — let the user click. Just focus the input.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resend OTP cooldown ticker — decrements every second while > 0.
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  return (
    <AuthLayout
      title="Sign in or create your FlexPay account."
      subtitle="One mobile number. One OTP. KYC sourced from hitch.zone. No paperwork."
      footer={
        <span>
          Need an admin or approver login?{" "}
          <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="link-login">
            Use password sign-in
          </Link>
        </span>
      }
    >
      <div className="space-y-5" data-testid="hitch-signup-wizard">
        <StepBar step={step} />

        {step === "mobile" && (
          <div className="space-y-4" data-testid="step-mobile">
            <div>
              <Label htmlFor="mobile">Mobile number</Label>
              <Input
                id="mobile"
                inputMode="numeric"
                maxLength={10}
                data-testid="input-mobile"
                placeholder="9876543210"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                className="mt-1 font-mono"
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-1">
                We&apos;ll send an OTP via hitch.zone. New accounts auto-create — no
                password needed.
              </p>
            </div>
            <div>
              <Label htmlFor="ref_code">
                Referral / Promo code{" "}
                <span className="text-muted-foreground font-normal text-xs">(optional)</span>
              </Label>
              <Input
                id="ref_code"
                data-testid="input-ref-code"
                placeholder="WELCOME"
                value={refCode}
                onChange={(e) =>
                  setRefCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))
                }
                className="mt-1 font-mono tracking-wider uppercase"
              />
            </div>
            <Button
              size="lg"
              className="w-full tracking-wide"
              onClick={() => sendOtp()}
              disabled={loading || mobile.length !== 10}
              data-testid="submit-mobile"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              Send OTP
            </Button>
          </div>
        )}

        {step === "otp" && (
          <div className="space-y-4" data-testid="step-otp">
            <div>
              <Label>OTP sent to +91 {mobile}</Label>
              <Input
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                className="mt-1 font-mono text-center text-lg tracking-widest"
                placeholder="••••••"
                data-testid="input-otp"
                autoFocus
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-muted-foreground">
                  OTPs from hitch.zone expire in about a minute.
                </p>
                <button
                  type="button"
                  onClick={() => sendOtp({ silent: true })}
                  disabled={loading || resendIn > 0}
                  className="text-xs text-primary font-medium hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
                  data-testid="resend-otp-btn"
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend OTP"}
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={reset}
                disabled={loading}
                data-testid="otp-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Change mobile
              </Button>
              <Button
                size="lg"
                className="flex-1 tracking-wide"
                onClick={verifyOtp}
                disabled={loading || otp.length < 4}
                data-testid="verify-otp-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Verify & continue
              </Button>
            </div>
          </div>
        )}

        {step === "pan" && (
          <div className="space-y-4" data-testid="step-pan">
            {kycInfo && (
              <div
                className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs"
                data-testid="kyc-status-banner"
              >
                <div className="flex items-center gap-1.5 font-medium text-amber-800">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Current KYC on hitch.zone:{" "}
                  <span className="font-mono">{kycInfo.verification_percentage}%</span>
                  <Badge variant="outline" className="ml-1 border-amber-300 text-amber-700">
                    needs ≥ {kycInfo.threshold}%
                  </Badge>
                </div>
                <p className="text-amber-900 mt-1">
                  Enter your PAN below — we&apos;ll verify it against the income-tax
                  database and refresh your KYC status.
                </p>
              </div>
            )}
            <div>
              <Label>PAN number</Label>
              <Input
                value={pan}
                onChange={(e) =>
                  setPan(e.target.value.toUpperCase().slice(0, 10))
                }
                placeholder="ABCDE1234F"
                maxLength={10}
                className="mt-1 font-mono uppercase tracking-wider"
                data-testid="input-pan"
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setStep("otp")}
                disabled={loading}
                data-testid="pan-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                size="lg"
                className="flex-1 tracking-wide"
                onClick={verifyPan}
                disabled={loading || pan.length !== 10}
                data-testid="verify-pan-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ShieldCheck className="h-4 w-4 mr-2" />
                )}
                Verify PAN
              </Button>
            </div>
          </div>
        )}

        {step === "done" && (
          <div
            className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 space-y-3"
            data-testid="step-done"
          >
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-semibold">
                {kycInfo?.verified ? "KYC verified" : "PAN accepted"}
              </span>
            </div>
            {resultUser && (
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Name</span>
                  <div className="font-semibold">
                    {resultUser.kyc?.full_name || resultUser.full_name || "—"}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Mobile</span>
                  <div className="font-mono">+91 {resultUser.mobile}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">PAN</span>
                  <div className="font-mono">{resultUser.kyc?.pan_number || "—"}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Verification</span>
                  <div className="font-mono">
                    {kycInfo?.verification_percentage ?? 0}%
                  </div>
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => navigate("/dashboard", { replace: true })}
                data-testid="go-dashboard-btn"
              >
                Go to dashboard
              </Button>
              <Button variant="outline" onClick={reset} data-testid="restart-btn">
                <RotateCcw className="h-3 w-3 mr-1" /> Start over
              </Button>
            </div>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
